import type { DatabaseEntryFieldValuesSnapshot } from '@databases/ai-first-creation-serializer/database-entry-field-values-snapshot';
import type { AiFieldStreamTarget } from '@databases/ai-first-creation-token-processing';
import {
  fetchDatabaseAiTeammate,
  iterateDatabaseAiTeammateDocumentParts
} from '@databases/ai-teammate-client';
import { collabStateManager } from '@databases/collab-state-manager';
import { logError } from '@databases/logs';

import { DatabaseStreamState } from './database-stream-state';
import { readDatabaseContent } from './handlers/read-database-content-handler';

type RunDatabaseAiFieldBaseOptions = {
  contentId: string;
  locale: string;
  prompt: string;
  abortSignal?: AbortSignal;
  entryFieldValuesSnapshot?: DatabaseEntryFieldValuesSnapshot;
};

type FieldAiTarget = Extract<AiFieldStreamTarget, { scope: 'field' }>;
type EntryAiTarget = Extract<AiFieldStreamTarget, { scope: 'entry' }>;

export type RunDatabaseAiFieldOptions = RunDatabaseAiFieldBaseOptions &
  (
    | {
        target: FieldAiTarget;
        contextScope: { kind: 'all-entries' } | { kind: 'empty-target-field-entries' };
      }
    | {
        target: EntryAiTarget;
      }
  );

function buildDatabaseEntryScope(options: RunDatabaseAiFieldOptions) {
  if (!('contextScope' in options)) {
    return {
      kind: 'specific-entries',
      entryIds: [options.target.entryId]
    } as const;
  }

  switch (options.contextScope.kind) {
    case 'all-entries':
      return { kind: 'all-entries' } as const;
    case 'empty-target-field-entries':
      return {
        kind: 'empty-ai-summary-entries',
        fieldId: options.target.fieldId
      } as const;
    default: {
      const unhandledScope: never = options.contextScope;
      throw new Error(`Unhandled AI field context scope: ${String(unhandledScope)}`);
    }
  }
}

function assertTargetExists(contentId: string, target: AiFieldStreamTarget): void {
  const database = collabStateManager.getCollabManager(contentId)?.database;
  if (!database) {
    throw new Error('Database not found');
  }

  if (!database.fieldsById[target.fieldId]) {
    throw new Error(`AI field target ${target.fieldId} not found`);
  }

  switch (target.scope) {
    case 'field':
      return;
    case 'entry':
      if (!database.entriesById[target.entryId]) {
        throw new Error(`AI field entry target ${target.entryId} not found`);
      }
      return;
    default: {
      const unhandledTarget: never = target;
      throw new Error(`Unhandled AI field target: ${String(unhandledTarget)}`);
    }
  }
}

export async function runDatabaseAiField(
  options: RunDatabaseAiFieldOptions
): Promise<void> {
  const { contentId, locale, prompt, target, abortSignal } = options;
  const streamState = DatabaseStreamState.getInstance();
  if (streamState.isStreamingActive) {
    throw new Error('Another database stream is already active');
  }

  assertTargetExists(contentId, target);

  const entryScope = buildDatabaseEntryScope(options);
  const content = readDatabaseContent(contentId, {
    includeAiFields: true,
    includeSelection: false,
    ...(options.entryFieldValuesSnapshot !== undefined && {
      entryFieldValuesSnapshot: options.entryFieldValuesSnapshot
    }),
    entryScope
  });
  if (content.contentType !== 'database') {
    throw new Error('Expected database content for AI field generation');
  }

  let reader: Awaited<ReturnType<typeof fetchDatabaseAiTeammate>> | undefined;
  let streamCompletionScheduled = false;

  try {
    streamState.clearErrors();
    streamState.startAiFieldStreaming(contentId, target);

    reader = await fetchDatabaseAiTeammate(
      {
        prompt,
        locale,
        databaseContext: {
          content_id: contentId,
          title: content.title,
          url: content.url,
          csv: content.csv
        }
      },
      abortSignal
    );

    for await (const documentPart of iterateDatabaseAiTeammateDocumentParts(reader)) {
      streamState.processChunk(documentPart);
    }

    await streamState.completeStreaming();
    streamCompletionScheduled = true;

    const errors = streamState.getErrors();
    if (errors.length > 0) {
      throw new Error(errors.map(({ message }) => message).join('; '));
    }

    streamState.markAiFieldRunCompleted();
    // eslint-disable-next-line canvas/no-swallow-errors-in-catch
  } catch (error) {
    // If completion has not scheduled its delayed reset, abort only this AI-field stream.
    if (!streamCompletionScheduled && streamState.isAiFieldStreamingActive) {
      streamState.abortStreaming();
    }

    if (reader) {
      try {
        await reader.cancel();
        // eslint-disable-next-line canvas/no-swallow-errors-in-catch
      } catch (cancelError) {
        logError(
          'Failed to cancel database AI field stream',
          cancelError instanceof Error ? cancelError : new Error(String(cancelError))
        );
      }
    }

    throw error;
  }
}
